import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ComplexTestBuilder.css';
import API_URL, { getUserPlatform } from '../api';
import { copyText } from '../utils/clipboard';
import ConfirmModal from './ConfirmModal';

const typeLabels = { desk: '🖥️ Сервіровка', game: '🎮 Гра', quiz: '📝 Квіз' };

const ComplexTestBuilder = () => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [steps, setSteps] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [targetCity, setTargetCity] = useState('');
    const [cities, setCities] = useState([]);
    const [filterCity, setFilterCity] = useState('');

    // Available items
    const [available, setAvailable] = useState({ templates: [], scenarios: [], quizzes: [] });
    const [savedTests, setSavedTests] = useState([]);
    const [copyStatus, setCopyStatus] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, idToDelete: null });
    const [searchDesk, setSearchDesk] = useState('');
    const [searchGame, setSearchGame] = useState('');
    const [searchQuiz, setSearchQuiz] = useState('');

    useEffect(() => {
        fetchUser();
        fetchData();
        fetchCities();
    }, []);

    const fetchUser = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchCities = async () => {
        try {
            const res = await axios.get(`${API_URL}/cities${getUserPlatform() ? `?platform=${getUserPlatform()}` : ''}`);
            setCities(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const [availRes, testsRes] = await Promise.all([
                axios.get(`${API_URL}/complex-tests/available-items`, config),
                axios.get(`${API_URL}/complex-tests`, config)
            ]);
            setAvailable(availRes.data);
            setSavedTests(testsRes.data);
        } catch (e) {
            console.error('Fetch error:', e);
        } finally {
            setLoading(false);
        }
    };

    const addStep = (type, item) => {
        const step = {
            type,
            refId: item._id,
            title: item.title || item.name || item.templateName,
            timeLimit: item.timeLimit || 0
        };
        setSteps(prev => [...prev, step]);
    };

    const removeStep = (index) => {
        setSteps(prev => prev.filter((_, i) => i !== index));
    };

    const moveStep = (index, dir) => {
        const newSteps = [...steps];
        const target = index + dir;
        if (target < 0 || target >= newSteps.length) return;
        [newSteps[index], newSteps[target]] = [newSteps[target], newSteps[index]];
        setSteps(newSteps);
    };

    const updateStepTime = (index, value) => {
        setSteps(prev => prev.map((step, i) => i === index ? { ...step, timeLimit: value } : step));
    };

    const handleSave = async () => {
        if (!title.trim()) { alert('Введіть назву тесту'); return; }
        if (steps.length === 0) { alert('Додайте хоча б один крок'); return; }

        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = {
                title: title.trim(),
                description: description.trim(),
                steps,
                targetCity: ['superadmin', 'admin', 'trainer'].includes(user?.role) ? targetCity : undefined
            };

            if (editingId) {
                await axios.put(`${API_URL}/complex-tests/${editingId}`, payload, config);
            } else {
                await axios.post(`${API_URL}/complex-tests`, payload, config);
            }

            setTitle('');
            setDescription('');
            setSteps([]);
            setTargetCity('');
            setEditingId(null);
            fetchData();
        } catch (e) {
            console.error('Save error:', e);
            const msg = e.response?.data?.error || e.message || 'Невідома помилка';
            alert(`Помилка при збереженні: ${msg}`);
        }
    };

    const handleEdit = (test) => {
        setEditingId(test._id);
        setTitle(test.title);
        setDescription(test.description || '');
        setSteps(test.steps || []);
        setTargetCity(test.targetCity || '');
    };

    const handleConfirmDelete = async () => {
        const id = confirmModal.idToDelete;
        if (!id) return;
        setConfirmModal({ isOpen: false, idToDelete: null });
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/complex-tests/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (editingId === id) {
                setEditingId(null); setTitle(''); setDescription(''); setSteps([]); setTargetCity('');
            }
            fetchData();
        } catch (e) {
            alert('Помилка при видаленні');
        }
    };

    const handleCopyLink = async (complexTestId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/complex-tests/links`, { complexTestId, usageMode: 'multi' }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const url = `${window.location.origin}/complex/${res.data.hash}`;
            await copyText(url);
            setCopyStatus(complexTestId);
            setTimeout(() => setCopyStatus(null), 3000);
        } catch (e) {
            console.error('handleCopyLink error:', e);
            alert('Помилка копіювання');
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setTitle('');
        setDescription('');
        setSteps([]);
        setTargetCity('');
    };

    const totalTime = steps.reduce((sum, s) => sum + (s.timeLimit || 0), 0);
    const deskCount = steps.filter(s => s.type === 'desk').length;
    const gameCount = steps.filter(s => s.type === 'game').length;
    const quizCount = steps.filter(s => s.type === 'quiz').length;

    const filteredTemplates = (available.templates || []).filter(t => 
        !searchDesk || (t.templateName || t.name || '').toLowerCase().includes(searchDesk.toLowerCase())
    );
    const filteredScenarios = (available.scenarios || []).filter(s => 
        !searchGame || (s.title || '').toLowerCase().includes(searchGame.toLowerCase())
    );
    const filteredQuizzes = (available.quizzes || []).filter(q => 
        !searchQuiz || (q.title || '').toLowerCase().includes(searchQuiz.toLowerCase())
    );

    if (loading) return <div className="placeholder-view">Завантаження...</div>;

    return (
        <div className="complex-builder">
            <header className="complex-builder-header">
                <div>
                    <h1>{editingId ? '✏️ Редагування тесту' : '🧩 Комплексний тест'}</h1>
                    <p>{editingId ? 'Змініть кроки та ліміти часу та оновіть тест' : 'Об\'єднайте сервіровки, ігри та квізи в єдиний навчальний шлях'}</p>
                </div>
                <div className="builder-actions">
                    {editingId && (
                        <button className="btn-header-ghost btn-cancel-edit" onClick={cancelEdit}>
                            Скасувати
                        </button>
                    )}
                    <button className="btn-save-template" onClick={handleSave}>
                        {editingId ? '💾 Оновити тест' : '💾 Зберегти тест'}
                    </button>
                </div>
            </header>

            <div className="builder-body">
                <div className="builder-main">
                    <div className="builder-form-card">
                        <div className="builder-field">
                            <label>Назва комплексного тесту</label>
                            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                                placeholder="Наприклад: Повний тест офіціанта (Стандарт)" />
                        </div>
                        <div className="builder-field">
                            <label>Опис тесту для студентів (необов'язково)</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)}
                                placeholder="Опишіть, які навички перевіряє цей комплексний тест..." rows={2} />
                        </div>

                        {['superadmin', 'admin', 'trainer'].includes(user?.role) && (
                            <div className="builder-field">
                                <label>📍 Призначити місту (залиште порожнім для доступу всім)</label>
                                <select
                                    value={targetCity}
                                    onChange={e => setTargetCity(e.target.value)}
                                    className="builder-city-select"
                                >
                                    <option value="">Всі міста</option>
                                    {cities.map(c => (
                                        <option key={c._id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Student Journey Timeline */}
                    {steps.length > 0 && (
                        <div className="journey-timeline-card">
                            <div className="journey-header">
                                <div className="steps-label">🗺️ Шлях студента (Student Journey)</div>
                                <div className="journey-summary">
                                    <span className="journey-badge desk">🍽️ {deskCount} сервіровок</span>
                                    <span className="journey-badge game">🎮 {gameCount} ігор</span>
                                    <span className="journey-badge quiz">📝 {quizCount} квізів</span>
                                    <span className="journey-time-badge">⏱️ Загальний час: {totalTime > 0 ? `${totalTime} хв` : 'Без обмежень'}</span>
                                </div>
                            </div>
                            <div className="journey-rail-wrapper">
                                <div className="journey-rail">
                                    <div className="journey-milestone start">
                                        <div className="milestone-dot">🟢</div>
                                        <div className="milestone-label">Старт</div>
                                    </div>
                                    
                                    {steps.map((step, idx) => (
                                        <React.Fragment key={idx}>
                                            <div className="journey-line" />
                                            <div className={`journey-milestone step-node ${step.type}`} title={step.title}>
                                                <div className="milestone-dot">
                                                    {step.type === 'desk' ? '🍽️' : step.type === 'game' ? '🎮' : '📝'}
                                                </div>
                                                <div className="milestone-info">
                                                    <span className="milestone-index">{idx + 1}</span>
                                                    <span className="milestone-title">{step.title}</span>
                                                    <span className="milestone-time">{step.timeLimit > 0 ? `${step.timeLimit} хв` : '∞'}</span>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                    
                                    <div className="journey-line" />
                                    <div className="journey-milestone end">
                                        <div className="milestone-dot">🏁</div>
                                        <div className="milestone-label">Сертифікат</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="steps-section">
                        <div className="steps-header">
                            <div className="steps-label">Кроки тесту ({steps.length})</div>
                        </div>
                        {steps.length === 0 ? (
                            <div className="empty-steps">
                                👈 Додайте кроки зі списку доступних матеріалів праворуч
                            </div>
                        ) : (
                            <div className="steps-list">
                                {steps.map((step, idx) => (
                                    <div key={idx} className="step-card">
                                        <div className="step-arrows">
                                            <button className="step-arrow" disabled={idx === 0}
                                                onClick={() => moveStep(idx, -1)}>▲</button>
                                            <button className="step-arrow" disabled={idx === steps.length - 1}
                                                onClick={() => moveStep(idx, 1)}>▼</button>
                                        </div>
                                        <div className={`step-number ${step.type}`}>{idx + 1}</div>
                                        <div className="step-info">
                                            <div className="step-title">{step.title}</div>
                                            <div className="step-type">
                                                {typeLabels[step.type]}
                                            </div>
                                        </div>
                                        
                                        <div className="step-time-setting">
                                            <span className="time-label">⏱️ Час:</span>
                                            <input
                                                type="range"
                                                min="0"
                                                max="60"
                                                step="1"
                                                value={step.timeLimit || 0}
                                                onChange={e => updateStepTime(idx, parseInt(e.target.value) || 0)}
                                                className="step-time-slider"
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                max="120"
                                                value={step.timeLimit || 0}
                                                onChange={e => updateStepTime(idx, parseInt(e.target.value) || 0)}
                                                className="step-time-input"
                                            />
                                            <span className="time-unit">хв {step.timeLimit === 0 && '(∞)'}</span>
                                        </div>

                                        <button className="step-remove" onClick={() => removeStep(idx)} title="Вилучити крок">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Saved tests */}
                    {savedTests.length > 0 && (
                        <div className="saved-tests">
                            <div className="saved-tests-header">
                                <div className="steps-label">Збережені комплексні тести ({savedTests.length})</div>
                                {['superadmin', 'admin', 'trainer'].includes(user?.role) && (
                                    <div className="city-filter-container">
                                        <span className="city-filter-label">📍 Фільтр міста:</span>
                                        <select
                                            value={filterCity}
                                            onChange={e => setFilterCity(e.target.value)}
                                            className="city-filter-select"
                                        >
                                            <option value="">Всі міста</option>
                                            {cities.map(c => (
                                                <option key={c._id} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                            {(() => {
                                const filteredTests = savedTests.filter(t => !filterCity || t.targetCity === filterCity);
                                if (filteredTests.length === 0) return <div className="empty-saved-msg">Немає знайдених тестів для цього міста</div>;

                                return (
                                    <div className="saved-tests-list">
                                        {filteredTests.map(test => (
                                            <div key={test._id} className="saved-test-card">
                                                <div className="saved-test-info">
                                                    <div className="test-title">
                                                        <span>🧩 {test.title}</span>
                                                        {test.targetCity && <span className="test-city-badge">📍 {test.targetCity}</span>}
                                                    </div>
                                                    <div className="test-meta">
                                                        {test.steps?.length || 0} кроків · ⏱️ {test.steps?.reduce((sum, s) => sum + (s.timeLimit || 0), 0)} хв всього
                                                    </div>
                                                </div>
                                                <div className="saved-test-actions">
                                                    {copyStatus === test._id ? (
                                                        <span className="saved-copied">Готово ✓</span>
                                                    ) : (
                                                        <button onClick={() => handleCopyLink(test._id)} title="Копіювати посилання для проходження">🔗 Посилання</button>
                                                    )}
                                                    <button onClick={() => handleEdit(test)} title="Редагувати">✏️</button>
                                                    <button className="btn-delete" onClick={() => setConfirmModal({ isOpen: true, idToDelete: test._id })} title="Видалити">🗑️</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {/* Right sidebar: available items with local filters */}
                <aside className="builder-sidebar">
                    <div className="sidebar-section">
                        <div className="sidebar-section-label">🖥️ Сервіровки ({filteredTemplates.length})</div>
                        <input
                            type="text"
                            placeholder="🔍 Пошук сервіровки..."
                            value={searchDesk}
                            onChange={e => setSearchDesk(e.target.value)}
                            className="sidebar-search-input"
                        />
                        <div className="sidebar-items">
                            {filteredTemplates.length === 0 ? (
                                <div className="sidebar-empty">Нічого не знайдено</div>
                            ) : filteredTemplates.map(t => (
                                <div key={t._id} className="sidebar-item" onClick={() => addStep('desk', t)} title="Додати до кроків">
                                    <span className="item-icon">🍽️</span>
                                    <span className="item-title">{t.templateName || t.name}</span>
                                    <span className="item-add-badge">+</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <div className="sidebar-section-label">🎮 Сценарії ({filteredScenarios.length})</div>
                        <input
                            type="text"
                            placeholder="🔍 Пошук сценарію..."
                            value={searchGame}
                            onChange={e => setSearchGame(e.target.value)}
                            className="sidebar-search-input"
                        />
                        <div className="sidebar-items">
                            {filteredScenarios.length === 0 ? (
                                <div className="sidebar-empty">Нічого не знайдено</div>
                            ) : filteredScenarios.map(s => (
                                <div key={s._id} className="sidebar-item" onClick={() => addStep('game', s)} title="Додати до кроків">
                                    <span className="item-icon">🎮</span>
                                    <span className="item-title">{s.title}</span>
                                    <span className="item-add-badge">+</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <div className="sidebar-section-label">📝 Квізи ({filteredQuizzes.length})</div>
                        <input
                            type="text"
                            placeholder="🔍 Пошук квізу..."
                            value={searchQuiz}
                            onChange={e => setSearchQuiz(e.target.value)}
                            className="sidebar-search-input"
                        />
                        <div className="sidebar-items">
                            {filteredQuizzes.length === 0 ? (
                                <div className="sidebar-empty">Нічого не знайдено</div>
                            ) : filteredQuizzes.map(q => (
                                <div key={q._id} className="sidebar-item" onClick={() => addStep('quiz', q)} title="Додати до кроків">
                                    <span className="item-icon">📝</span>
                                    <span className="item-title">{q.title}</span>
                                    <span className="item-add-badge">+</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title="Видалення комплексного тесту"
                message="Ви впевнені, що хочете видалити цей комплексний тест? Цю дію неможливо скасувати."
                confirmText="Видалити"
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmModal({ isOpen: false, idToDelete: null })}
            />
        </div>
    );
};

export default ComplexTestBuilder;
